'use client';

import Link from 'next/link';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBlogSource,
  useBlockPart,
  type Article,
  type BlogPagination,
} from '@akademi/site-builder-runtime';
import { resolveArticleCard, type ArticleCardVariant } from '../cards/ArticleCard';
import { sectionPad, sectionInner } from '../_shared/styles';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Paginated article grid for the discovery surface. Reads
 * {@code ?page=} and {@code ?category=} from the URL itself
 * (operators don't have to thread search params through).
 *
 * <p>{@code skipFirst} is the "coexists with BlogFeaturedHero" knob:
 * when true AND we're on page 1 with no active category filter, the
 * grid fetches one extra article and slices it off so the hero's
 * lead article isn't duplicated below.
 */
export interface BlogArticleGridProps {
  variant?: ArticleCardVariant;
  pageSize?: number;
  showAuthor?: boolean;
  showReadTime?: boolean;
  skipFirst?: boolean;
  showPagination?: boolean;
  /** href template per article. Default {@code /blog/{slug}}. */
  articleHrefTemplate?: string;
  /** Base path for pagination + filter clear links. Default
   *  {@code /blog/explore}. */
  exploreHref?: string;
}

interface BlogArticleGridData {
  items: Article[];
  pagination: BlogPagination;
  activeCategory?: string;
  isLead: boolean;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function fillTemplate(tpl: string, slug: string): string {
  return tpl.replace('{slug}', encodeURIComponent(slug));
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function prettify(s: string): string {
  return s
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase() + p.slice(1))
    .join(' ');
}

function useSearchParam(name: string): string | undefined {
  // Lightweight client-side URL reader so the block doesn't depend on
  // next/navigation directly (works in any React host).
  if (typeof window === 'undefined') return undefined;
  return new URLSearchParams(window.location.search).get(name) ?? undefined;
}

export function BlogArticleGrid({
  variant = 'image-led',
  pageSize: rawPageSize = 12,
  showAuthor = true,
  showReadTime = true,
  skipFirst = false,
  showPagination = true,
  articleHrefTemplate = '/blog/{slug}',
  exploreHref = '/blog/explore',
}: BlogArticleGridProps) {
  const blog = useBlogSource();
  const part = useBlockPart();
  const pageSize = clamp(rawPageSize, 1, 48);
  const pageRaw = useSearchParam('page');
  const category = useSearchParam('category');
  const page = useMemo(() => {
    const p = parseInt(pageRaw ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  }, [pageRaw]);
  const isLead = page === 1 && !category;
  const doSkipFirst = isLead && skipFirst;
  const fetchSize = doSkipFirst ? pageSize + 1 : pageSize;

  const state = useAsyncResource<BlogArticleGridData>(async () => {
    if (!blog) return null;
    const filters: Record<string, Record<string, string | number | boolean>> = {};
    if (category) filters['category'] = { $eq: category };
    const { items, pagination } = await blog.listArticles({
      filters: Object.keys(filters).length ? filters : undefined,
      pagination: { page, pageSize: fetchSize },
    });
    const grid = doSkipFirst ? items.slice(1) : items;
    return {
      items: grid,
      pagination: { ...pagination, pageSize },
      ...(category ? { activeCategory: category } : {}),
      isLead,
    };
  }, [blog, page, category, fetchSize, doSkipFirst, isLead, pageSize]);

  if (!blog) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="BlogArticleGrid" adapterName="BlogSource" />
        </div>
      </BlockRoot>
    );
  }

  if (state.kind === 'loading') {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <SkeletonRow count={pageSize} layout="grid" minCardWidth={280} />
        </div>
      </BlockRoot>
    );
  }

  if (state.kind === 'error' || state.kind === 'empty') {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          {category ? <EmptyWithClear exploreHref={exploreHref} category={category} /> : <Empty />}
        </div>
      </BlockRoot>
    );
  }

  const ArticleCard = resolveArticleCard(variant);
  const data = state.data;

  return (
    <BlockRoot as="section" style={sectionPad}>
      <div style={sectionInner}>
        {data.activeCategory && (
          <ActiveFilterStrip category={prettify(data.activeCategory)} clearHref={exploreHref} />
        )}

        <div
          {...part('list')}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 'var(--space-5, 20px)',
          }}
        >
          {data.items.map((a) => (
            <ArticleCard
              key={a.id}
              href={fillTemplate(articleHrefTemplate, a.slug)}
              title={a.title}
              excerpt={a.excerpt}
              date={formatDate(a.publishedAt)}
              author={showAuthor ? a.author?.name : undefined}
              category={prettify(a.category)}
              readTime={showReadTime ? a.readTime : undefined}
              cover={a.cover}
            />
          ))}
        </div>

        {showPagination && (
          <Pagination
            exploreHref={exploreHref}
            page={data.pagination.page}
            pageCount={data.pagination.pageCount}
            activeCategory={data.activeCategory}
          />
        )}
      </div>
    </BlockRoot>
  );
}

function ActiveFilterStrip({ category, clearHref }: { category: string; clearHref: string }) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  return (
    <div
      {...part('filter')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 'var(--space-5, 20px)',
        padding: 'var(--space-2, 8px) var(--space-3, 12px)',
        background: 'var(--color-surface-card, #fff)',
        border: '1px solid var(--color-neutral-200, #e4e4e7)',
        borderRadius: 'var(--radius-md, 8px)',
        fontSize: '13px',
        color: 'var(--color-text-secondary, #71717a)',
      }}
    >
      <span>{t('blog.filter.active', { category })}</span>
      <span style={{ marginLeft: 'auto' }}>
        <Link
          href={clearHref}
          {...part('action')}
          style={{ color: 'var(--color-brand-500, #3b5bdb)', textDecoration: 'none' }}
        >
          {t('blog.filter.clear')}
        </Link>
      </span>
    </div>
  );
}

function Pagination({
  exploreHref,
  page,
  pageCount,
  activeCategory,
}: {
  exploreHref: string;
  page: number;
  pageCount: number;
  activeCategory?: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  if (pageCount <= 1) return null;
  const buildHref = (p: number) => {
    const params = new URLSearchParams();
    if (activeCategory) params.set('category', activeCategory);
    if (p > 1) params.set('page', String(p));
    const qs = params.toString();
    return `${exploreHref}${qs ? `?${qs}` : ''}`;
  };
  const btnStyle: React.CSSProperties = {
    padding: 'var(--space-1-5, 6px) var(--space-3, 12px)',
    fontSize: '13px',
    border: '1px solid var(--color-neutral-200, #e4e4e7)',
    borderRadius: 'var(--radius-md, 8px)',
    color: 'var(--color-text-primary, #18181b)',
    textDecoration: 'none',
    background: 'var(--color-surface-card, #fff)',
  };
  return (
    <nav
      aria-label={t('blog.pagination.label')}
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
        marginTop: 'var(--space-8, 32px)',
      }}
    >
      {page > 1 && (
        <Link href={buildHref(page - 1)} style={btnStyle} {...part('pager', 0)}>
          {t('blog.pagination.newer')}
        </Link>
      )}
      <span
        style={{
          alignSelf: 'center',
          fontSize: '13px',
          color: 'var(--color-text-secondary, #71717a)',
        }}
      >
        {t('blog.pagination.position', { page, pageCount })}
      </span>
      {page < pageCount && (
        <Link href={buildHref(page + 1)} style={btnStyle} {...part('pager', 1)}>
          {t('blog.pagination.older')}
        </Link>
      )}
    </nav>
  );
}

function Empty() {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  return (
    <div
      {...part('empty')}
      style={{
        padding: 'var(--space-16, 64px) var(--space-4, 16px)',
        textAlign: 'center',
        color: 'var(--color-text-secondary, #6b7280)',
        fontSize: 'var(--font-size-sm, 14px)',
      }}
    >
      {t('blog.empty.none')}
    </div>
  );
}

function EmptyWithClear({ exploreHref, category }: { exploreHref: string; category: string }) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  return (
    <div
      {...part('empty')}
      style={{
        padding: 'var(--space-12, 48px) var(--space-4, 16px)',
        textAlign: 'center',
        color: 'var(--color-text-secondary, #6b7280)',
        fontSize: 'var(--font-size-sm, 14px)',
      }}
    >
      <p style={{ margin: '0 0 12px' }}>
        {t('blog.empty.inCategory', { category: prettify(category) })}
      </p>
      <Link
        href={exploreHref}
        style={{
          color: 'var(--color-brand-500, #3b5bdb)',
          textDecoration: 'none',
          fontSize: 'var(--font-size-sm, 14px)',
        }}
      >
        {t('blog.empty.clearFilter')}
      </Link>
    </div>
  );
}
